<template>
    <div :class="[{'calendar-container': bordered}]">

        <!-- CALENDAR -->
        <v-slide-y-transition>
            <v-col cols="12" :md="forceCalendar ? 12 : 6" v-if="showCalendarComputed">
                <v-date-picker
                    v-model="calendar" :events="daysWithRoutines"
                    color="#CCFF99" event-color="grey darken-1"
                    locale="es-CL" first-day-of-week="1"
                    full-width no-title multiple :show-current="false"
                    :picker-date.sync="pickerDate"
                />
                <v-divider class="divider-primary"/>
            </v-col>
        </v-slide-y-transition>

        <v-row v-if="isCompany">
            <v-col cols="12">
                <kimsa-combobox
                    :placeholder="$t('inputs.coaches_placeholder')"
                    outlined dense solo
                    :items="coaches"
                    @change="setCurrentCoach"
                    :value="currentCoach"
                    item-value="objectID"
                    clearable searchable
                    icon="$account"
                />
            </v-col>
            <v-col v-if="!currentCoach">
                <v-alert color="secondary" outlined icon="mdi-alert-circle-outline" prominent border="left">
                    {{ $t('admin.select_coach') }}
                </v-alert>
            </v-col>
        </v-row>

        <loading-spinner :no-margin-top="homeView" :line="homeView" :height="homeView ? '30px' : '200px'" v-if="loading"/>
        <v-row v-else>
            <!-- ROUTINES -->
            <v-col cols="12" v-if="filteredRoutines.length > 0">
                <v-expansion-panels focusable accordion class="kimsa-elevation" flat v-model="openFirst">
                    <v-expansion-panel v-for="(date,i) in filteredRoutines" :key="i">
                        <v-expansion-panel-header :class="['title-date', {'title-date-separator': i !== 0}]" ripple>
                            {{ date.name }}
                            <span class="quantity-routines">
                                ({{ date.routines.length }})
                            </span>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-sheet height="28" color="transparent"/>
                            <v-row>
                                <v-col v-for="(routine, j) in date.routines" :key="j" cols="12" class="mb-1">
                                    <routine-card
                                        :student-id="routine.studentId"
                                        :routine="routine"
                                        @click="goToAssignedRoutine(routine)"
                                    />
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>

            <!-- NO ROUTINES -->
            <v-col cols="12" v-if="!homeView && showNoRoutines" class="text-center mb-3">
                <v-img eager max-width="240px" class="mx-auto" :src="require('@/assets/img/card_placeholder.png')"/>
                <div class="subtext mb-3">
                    {{ $t('admin.no_assigned_routines') }}
                </div>
                <v-btn color="primary" class="black--text normal-btn" depressed :to="{name: 'coach.routines'}">
                    {{ $t('coach.see_routines') }}
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>

import {mapState, mapActions, mapMutations} from 'vuex'
import RoutineCard from '@/components/common/routines/RoutineCard'
import _ from "lodash";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import {Client, UserIndex} from "@/utils/Algolia";

export default {
    name: "CalendarMobile",
    components: {
        KimsaCombobox,
        LoadingSpinner,
        RoutineCard,
    },
    props: {
        forceCalendar: {
            type: Boolean,
            default: false,
        },
        defaultDate: {
            type: String,
            default: null
        },
        bordered: {
            type: Boolean,
            default: false,
        },
        homeView: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            openFirst: 0,
            calendar: [],
            pickerDate: null,
            routines: [],
            coaches: [],
            currentCoach: null,
        }
    },
    computed: {
        ...mapState(['showCalendar', 'coachAssignedRoutines']),
        showCalendarComputed(vm = this) {
            return vm.showCalendar || vm.forceCalendar
        },
        filteredRoutines(vm = this) {
            if (vm.calendar.length === 0) return vm.routines
            return vm.routines.filter(d => vm.calendar.includes(d.date))
        },
        noRoutines(vm = this) {
            return vm.coachAssignedRoutines.length === 0
        },
        isCompany(vm = this) {
            return vm.account?.accountType === 'company'
        },
        coachId(vm = this) {
            return vm.isCompany ?
                vm.currentCoach?.objectID || null :
                vm.currentUser.uid
        },
        showNoRoutines(vm = this) {
            if (!vm.isCompany && vm.noRoutines) return true
            if (vm.isCompany && vm.currentCoach && vm.noRoutines) return true
            return false
        },
    },
    async mounted() {
        const vm = this
        if (vm.isCompany) {
            await vm.loadCoaches()
        } else {
            if (vm.noRoutines) await vm.loadRoutines()
            vm.groupRoutines()
        }
    },
    methods: {
        ...mapMutations(['setShowCalendar']),
        ...mapActions(['bindCoachAssignedRoutines']),
        async loadCoaches() {
            const vm = this
            let filter = `coachSubscription.companyId: ${vm.currentUser.uid} AND coachSubscription.paused != 1`
            await Client.clearCache()
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 10000,
            })
                .then(({hits}) => {
                    vm.coaches = hits.map(hit => ({...hit, name: hit.name || hit.email}))
                    let coachId = vm.$route.query.coachId
                    if (coachId) vm.currentCoach = vm.coaches.find(coach => coach.objectID === coachId)
                })
                .catch(err => {
                    console.log(err);
                });
        },
        async loadRoutines(date = null) {
            const vm = this
            vm.loading = true
            let currentDate = vm.$moment().startOf('month').format('YYYY-MM-DD')
            if (date) currentDate = vm.$moment(date).startOf('month').format('YYYY-MM-DD')
            let payload = {coachId: vm.coachId, date: currentDate}
            if (vm.$route.query.studentId) payload.studentId = vm.$route.query.studentId
            await vm.bindCoachAssignedRoutines(payload)
            vm.loading = false
        },
        daysWithRoutines(date) {
            const vm = this
            let result = []
            vm.routines.forEach(data => {
                if (data.date.includes(date)) result.push('grey darken-1')
            })
            return result
        },
        goToAssignedRoutine(routine) {
            const vm = this
            if (vm.homeView) return vm.$router.push({name: `${vm.accountType}.routines.edit.assigned`, params: {uid: routine.id}})
            vm.$router.push({name: `${vm.accountType}.calendar.assignedRoutine`, params: {uid: routine.id}})
        },
        groupRoutines() {
            const vm = this
            let routines = _.groupBy(vm.coachAssignedRoutines.map(routine => {
                return {
                    ...routine,
                    id: routine.id,
                    date: routine.rawDate.full,
                    dateString: vm.$moment(routine.rawDate.full).format('D [de] MMMM')
                }
            }), 'dateString')

            vm.routines = Object.entries(routines).map(routine => {
                return {name: routine[0], date: routine[1][0].date, routines: routine[1]}
            })
        },
        setCurrentCoach(coach) {
            const vm = this
            if (coach?.name) {
                vm.currentCoach = coach
            } else if (coach) {
                let foundCoach = vm.coaches?.find(co => co.name.toLowerCase() === coach.toLowerCase()) || null
                foundCoach ? vm.currentCoach = foundCoach : vm.currentCoach = null
            } else {
                vm.currentCoach = null
            }
        },
    },
    watch: {
        async currentCoach() {
            const vm = this
            await vm.loadRoutines()
            vm.groupRoutines()
        },
        coachAssignedRoutines() {
            this.groupRoutines()
        },
        async pickerDate(val) {
            const vm = this
            await vm.loadRoutines(val)
            vm.groupRoutines()
            if(!vm.defaultDate) vm.calendar = []
        },
        defaultDate(val) {
            if(val) this.calendar = [val]
        }
    },
    beforeRouteLeave(to, from, next) {
        const vm = this
        vm.setShowCalendar(false)
        next()
    }
}
</script>

<style scoped>

.calendar-container {
    border: thin solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    overflow: hidden;
}

.title-date {
    color: var(--v-accent-base);
    font-weight: bold;
    border-radius: 4px;
}

.title-date-separator {
    border-top: 1px solid rgba(51, 51, 51, 0.1);
}

.quantity-routines {
    font-size: 9px;
    font-weight: normal;
    margin-left: 4px;
}

::v-deep .v-date-picker-table {
    height: 100%;
}

::v-deep .v-date-picker-table .v-btn.v-btn--active {
    color: #333333;
    box-shadow: 3px 3px 6px 0 rgba(1, 55, 46, 0.1) !important;
    border: thin solid rgba(0, 0, 0, 0.12) !important;
}

::v-deep .v-date-picker-table .v-btn.v-btn--active .v-btn__content {
    font-weight: 700 !important;
}

::v-deep .v-date-picker-table .v-btn.v-btn--active:before {
    opacity: 0 !important;
}

::v-deep .v-date-picker-table .v-btn.v-btn--rounded {
    border-radius: 5px;
    padding-bottom: 8px;
}

::v-deep .v-date-picker-table__events {
    height: 12px;
}

::v-deep .v-date-picker-table__events > .grey {
    height: 5px;
    width: 5px;
}

</style>